import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import useApi from "../../hooks/useApi";
import CustomConfirmDialog from "../../components/dialog/ConfirmDialog";

interface Reply {
  replyID: number;
  commentID: number;
  replyText: string;
  createdBy: number;
  createdByName: string;
  createdDate: string;
  updatedBy?: number | null;
  updatedByName?: string | null;
  updatedDate?: string | null;
}

interface Comment {
  commentID: number;
  feeStageId?: number;
  monthYear?: string;
  commentText: string;
  createdBy: number;
  createdByName: string;
  createdDate: string;
  updatedBy?: number | null;
  updatedByName?: string | null;
  updatedDate?: string | null;
  displayType?: number;
  replies?: Reply[];
}

interface CommentResponseDto {
  commentID: number;
  createdByName: string;
  createdDate: string;
}

interface ForecastCommentModalProps {
  open: boolean;
  onClose: (commentAdded?: boolean, commentText?: string) => void;
  feestage: any | null;
}

const ForecastCommentModal: React.FC<ForecastCommentModalProps> = ({
  open,
  onClose,
  feestage,
}) => {
  const [commentText, setCommentText] = useState("");
  const [comments, setComments] = useState<Comment[]>([]);
  const [replyText, setReplyText] = useState<{ [key: string]: string }>({});
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingReplyId, setEditingReplyId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [activeReplyBox, setActiveReplyBox] = useState<number | null>(null);

  // ✅ State for confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: "comment" | "reply" | null;
    commentID?: number;
    parentID?: number;
    replyID?: number;
  }>({ open: false, type: null });


  const { execute } = useApi();
  const feeStageId = useMemo(() => feestage?.feeStageID ?? 0, [feestage]);
  const monthYear = useMemo(() => {
    if (!feestage) return "";
    const [monthStr, year] = feestage.month.split("-");
    const monthMap: { [key: string]: string } = {
      Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
      Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12"
    };
    return `${year}-${monthMap[monthStr]}-01`;
  }, [feestage]);

  const displayType = feestage?.displayType ?? 1;
  const userId = Number(localStorage.getItem("userId"));
  const role = localStorage.getItem("role");
  const isAdmin = role?.toLowerCase() === "admin";
  const getReplyText = (id: number) => replyText[id.toString()] || "";
  const setReply = (id: number, text: string) =>
    setReplyText((prev) => ({ ...prev, [id.toString()]: text }));
  const [currentUserName, setCurrentUserName] = useState("");

  const fetchComments = async () => {
    if (!feestage) return;
    try {
      const { data } = await execute<Comment[]>(
        "get",
        `/IFS/GetComments?feeStageId=${feeStageId}&monthYear=${monthYear}&displayType=${displayType}`
      );
      if (data) setComments(data.map((c) => ({ ...c, replies: c.replies || [] })));

    } catch (err) {
      console.error("Error fetching comments", err);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [feestage, feeStageId, monthYear, displayType, execute]);

  // 🔹 Add comment
  const handleAddComment = async () => {
    if (!commentText.trim() || !feestage) return;
    const payload = { feeStageId, monthYear, commentText, createdBy: userId, displayType };
    try {
      const { data } = await execute<CommentResponseDto>("post", "/IFS/add-comment", payload);
      if (data) {
        setComments([
          {
            ...payload,
            commentID: data.commentID,
            createdDate: data.createdDate,
            replies: [],
            createdByName: data.createdByName,
          },
          ...comments,
        ]);
        setCommentText("");
      }
    } catch (err) {
      console.error("Error adding comment", err);
    }
  };

  // 🔹 Add reply (API returns full replies array)
  const handleAddReply = async (parentID: number) => {
    const text = replyText[parentID]?.trim();
    if (!text) return;
    const payload = { commentID: parentID, replyText: text, createdBy: userId };

    try {
      const { data } = await execute<Reply[]>(
        "post",
        `/IFS/AddReply?commentID=${parentID}`,
        payload
      );
      if (data) {
        setComments((prev) =>
          prev.map((c) =>
            c.commentID === parentID ? { ...c, replies: data } : c
          )
        );
        setReplyText((prev) => {
          const copy = { ...prev };
          delete copy[parentID.toString()];
          return copy;
        });
        setActiveReplyBox(null);
        setExpanded((prev) => new Set(prev).add(parentID));
      }
    } catch (err) {
      console.error("Error adding reply:", err);
    }
  };

  // 🔹 Trigger confirm dialog for delete comment
  const handleDeleteComment = (commentID: number) => {
    setConfirmDialog({ open: true, type: "comment", commentID });
  };

  // 🔹 Trigger confirm dialog for delete reply
  const handleDeleteReply = (parentID: number, replyID: number) => {
    setConfirmDialog({ open: true, type: "reply", parentID, replyID });
  };
  // 🔹 Confirm delete action
  const confirmDelete = async () => {
    try {
      if (confirmDialog.type === "comment" && confirmDialog.commentID) {
        await execute("delete", `/IFS/DeleteComment?commentID=${confirmDialog.commentID}`);
        setComments((prev) => prev.filter((c) => c.commentID !== confirmDialog.commentID));
      } else if (confirmDialog.type === "reply" && confirmDialog.parentID && confirmDialog.replyID) {
        await execute("delete", `/IFS/DeleteReply?replyID=${confirmDialog.replyID}`);
        setComments((prev) =>
          prev.map((c) =>
            c.commentID === confirmDialog.parentID
              ? { ...c, replies: c.replies?.filter((r) => r.replyID !== confirmDialog.replyID) }
              : c
          )
        );
      }
    } catch (err) {
      console.error("Error deleting:", err);
    } finally {
      setConfirmDialog({ open: false, type: null });
    }
  };

  // 🔹 Cancel delete
  const cancelDelete = () => {
    setConfirmDialog({ open: false, type: null });
  };

  // 🔹 Delete comment
  // const handleDeleteComment = async (commentID: number) => {
  //   if (!window.confirm("Do you want to delete this comment?")) return;

  //   try {
  //     // Call API to delete
  //     await execute("delete", `/IFS/DeleteComment?commentID=${commentID}`);

  //     // Update local state
  //     setComments((prev) => prev.filter((c) => c.commentID !== commentID));
  //   } catch (err) {
  //     console.error("Error deleting comment:", err);
  //   }
  // };

  //   // 🔹 Delete reply
  // const handleDeleteReply = async (parentID: number, replyID: number) => {
  //   if (!window.confirm("Do you want to delete this reply?")) return;

  //   try {
  //     // Call API to delete
  //     await execute("delete", `/IFS/DeleteReply?replyID=${replyID}`);

  //     // Update local state
  //     setComments((prev) =>
  //       prev.map((c) =>
  //         c.commentID === parentID
  //           ? { ...c, replies: c.replies?.filter((r) => r.replyID !== replyID) }
  //           : c
  //       )
  //     );
  //   } catch (err) {
  //     console.error("Error deleting reply:", err);
  //   }
  // };

  // 🔹 Edit comment & reply
  const handleEditComment = (commentID: number, text: string) => {
    setEditingCommentId(commentID);
    setEditingText(text);
  };
  const handleSaveEditComment = async () => {
    if (editingCommentId === null || !editingText.trim()) return;
    try {
      await execute(
        "put",
        `/IFS/UpdateComment?commentId=${editingCommentId}&commentText=${encodeURIComponent(
          editingText
        )}&userId=${userId}`
      );
      setComments((prev) =>
        prev.map((c) =>
          c.commentID === editingCommentId
            ? {
              ...c,
              commentText: editingText,
              updatedBy: userId,
              updatedByName: "You",
              updatedDate: new Date().toISOString()
            }
            : c
        )
      );
      setEditingCommentId(null);
      setEditingText("");
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditReply = (replyID: number, text: string) => {
    setEditingReplyId(replyID);
    setEditingText(text);
  };
  const handleSaveEditReply = async (parentID: number) => {
    if (editingReplyId === null || !editingText.trim()) return;
    try {
      await execute(
        "put",
        `/IFS/UpdateReply?replyId=${editingReplyId}&replyText=${encodeURIComponent(
          editingText
        )}&userId=${userId}`
      );
      setComments((prev) =>
        prev.map((c) =>
          c.commentID === parentID
            ? {
              ...c,
              replies: c.replies?.map((r) =>
                r.replyID === editingReplyId
                  ? {
                    ...r,
                    replyText: editingText,
                    updatedBy: userId,
                    updatedByName: "You",
                    updatedDate: new Date().toISOString()
                  }
                  : r
              ),
            }
            : c
        )
      );
      setEditingReplyId(null);
      setEditingText("");
    } catch (err) {
      console.error(err);
    }
  };

  const parentComments = comments.sort(
    (a, b) =>
      Number(new Date(b.createdDate)) - Number(new Date(a.createdDate))
  );

  return (
    <>
      <Dialog open={open} onClose={() => { }} fullWidth maxWidth="lg">
        <DialogTitle>
          {feestage
            ? `${feestage.feeStageReferenceNumber} - ${feestage.description} (${feestage.month})`
            : "Forecast Comments"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Add a comment..."
            fullWidth
            multiline
            rows={2}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            InputProps={{
              endAdornment: (
                <Box
                  sx={{ display: "flex", alignItems: "center", height: "100%" }}
                >
                  <Button
                    onClick={handleAddComment}
                    variant="contained"
                    sx={{ ml: 1 }}
                    disabled={!commentText.trim()}
                  >
                    Post
                  </Button>
                </Box>
              ),
            }}
          />
          <Box mt={3}>
            {parentComments.map((parent, index) => {
              const childReplies = parent.replies || [];
              const isExpanded = expanded.has(parent.commentID);
              const toggleExpand = (commentID: number) => {
                setExpanded((prev) => {
                  const newSet = new Set(prev);
                  newSet.has(commentID)
                    ? newSet.delete(commentID)
                    : newSet.add(commentID);
                  return newSet;
                });
              };
              return (
                <Box
                  key={parent.commentID}
                  mb={2}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: index % 2 === 0 ? "#f5f5f5" : "white",
                  }}
                >
                  <Typography
                    sx={{
                      cursor: childReplies.length > 0 ? "pointer" : "default",
                    }}
                    onClick={() =>
                      childReplies.length > 0 && toggleExpand(parent.commentID)
                    }
                  >

                    <small>
                      {parent.updatedDate && parent.updatedByName ? (
                        <span style={{ color: "#555" }}>
                          <strong>{parent.createdByName}</strong>{" "}
                          Edited by {parent.updatedBy === userId ? "You" : parent.updatedByName} on{" "}
                          {new Date(parent.updatedDate).toLocaleString()}
                        </span>
                      ) : (
                        <>
                          <strong>{parent.createdByName}</strong>{" "}
                          {new Date(parent.createdDate).toLocaleString()}
                        </>

                      )}
                    </small>

                    {/* <strong>{parent.createdByName}</strong>{" "}
                    <small>
                      {new Date(parent.createdDate).toLocaleString()}
                      {parent.updatedDate && parent.updatedByName && (
                        <em style={{ marginLeft: 6, color: "#555" }}>
                          (Edited by {parent.updatedByName} on{" "}
                          {new Date(parent.updatedDate).toLocaleString()})
                        </em>
                      )}
                    </small> */}
                    {childReplies.length > 0 && (
                      <Button size="small" sx={{ ml: 1 }}>
                        {isExpanded
                          ? "Hide Replies"
                          : `View Replies (${childReplies.length})`}
                      </Button>
                    )}
                  </Typography>

                  {editingCommentId === parent.commentID ? (
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                      />
                      <Button
                        size="small"
                        onClick={handleSaveEditComment}
                        sx={{ mt: 0.5 }}
                      >
                        Save
                      </Button>
                    </Box>
                  ) : (
                    <Typography>{parent.commentText}</Typography>
                  )}

                  <Box mt={0.5}>
                    <Button
                      size="small"
                      onClick={() =>
                        setActiveReplyBox(
                          activeReplyBox === parent.commentID
                            ? null
                            : parent.commentID
                        )
                      }
                    >
                      Reply
                    </Button>

                    {/* ✅ Admin can delete any comment; users can delete own if no replies */}
                    {(parent.createdBy === userId) && editingCommentId !== parent.commentID && (
                      <Button
                        size="small"
                        onClick={() => handleEditComment(parent.commentID, parent.commentText)}
                      >
                        Edit
                      </Button>
                    )}

                    {/* Delete: only admin or creator if no replies */}
                    {((parent.createdBy === userId && (parent.replies?.length ?? 0) === 0)) &&
                      editingCommentId !== parent.commentID && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleDeleteComment(parent.commentID)}
                        >
                          Delete
                        </Button>
                      )}
                  </Box>

                  {isExpanded &&
                    childReplies.map((reply) => (
                      <Box key={reply.replyID} ml={2} mt={1} mb={1}>
                        <Typography>
                          <small>
                            {reply.updatedDate && reply.updatedByName ? (
                              <span style={{ color: "#555" }}>
                                <strong>{reply.createdByName}</strong>{" "}
                                Edited by {reply.updatedBy === userId ? "You" : reply.updatedByName} on{" "}
                                {new Date(reply.updatedDate).toLocaleString()}
                              </span>
                            ) : (
                              <>
                                <strong>{reply.createdByName}</strong>{" "}
                                {new Date(reply.createdDate).toLocaleString()}
                              </>
                            )}
                          </small>



                          {/* <strong>{reply.createdByName}</strong>{" "}
                          <small>
                            {new Date(reply.createdDate).toLocaleString()}
                            {reply.updatedDate && reply.updatedByName && (
                              <em style={{ marginLeft: 6, color: "#555" }}>
                                (Edited by {reply.updatedByName} on{" "}
                                {new Date(reply.updatedDate).toLocaleString()})
                              </em>
                            )}
                          </small> */}
                        </Typography>
                        {editingReplyId === reply.replyID ? (
                          <Box>
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              value={editingText}
                              onChange={(e) => setEditingText(e.target.value)}
                            />
                            <Button
                              size="small"
                              onClick={() => handleSaveEditReply(parent.commentID)}
                              sx={{ mt: 0.5 }}
                            >
                              Save
                            </Button>
                          </Box>
                        ) : (
                          <Typography>{reply.replyText}</Typography>
                        )}

                        {/* ✅ Admin can delete any reply; users can only delete their own */}
                        {(reply.createdBy === userId) &&
                          editingReplyId !== reply.replyID && (
                            <Box mt={0.5}>
                              {reply.createdBy === userId && (
                                <Button
                                  size="small"
                                  onClick={() =>
                                    handleEditReply(reply.replyID, reply.replyText)
                                  }
                                >
                                  Edit
                                </Button>
                              )}
                              <Button
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleDeleteReply(parent.commentID, reply.replyID)
                                }
                              >
                                Delete
                              </Button>
                            </Box>
                          )}
                      </Box>
                    ))}

                  {activeReplyBox === parent.commentID && (
                    <Box mt={1} ml={childReplies.length > 0 ? 2 : 0}>
                      <TextField
                        placeholder="Reply..."
                        fullWidth
                        size="small"
                        value={getReplyText(parent.commentID)}
                        onChange={(e) =>
                          setReplyText({
                            ...replyText,
                            [parent.commentID.toString()]: e.target.value,
                          })
                        }
                      />
                      <Button
                        size="small"
                        onClick={() => handleAddReply(parent.commentID)}
                        sx={{ mt: 0.5 }}
                      >
                        Post Reply
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* 🔹 Custom Confirm Dialog */}
      <CustomConfirmDialog
        open={confirmDialog.open}
        title="Confirm Deletion"
        message={
          confirmDialog.type === "comment"
            ? "Do you really want to delete this comment?"
            : "Do you really want to delete this reply?"
        }
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        onClose={cancelDelete}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </>
  );
};

export default ForecastCommentModal;
